/*global $:false */

import AdvancedSearchM from '../model/recordm.model.advancedsearch.js';
import BaseM from '../model/recordm.model.entity.js';

import { DateF }  from '../fields/recordm.fields.date.js';
import { DateTime } from '../fields/recordm.fields.datetime.js';
import { NumberF } from '../fields/recordm.fields.number.js';

import Select from '../fields/recordm.fields.select.js';
import { TasksSearch } from '../fields/recordm.fields.tasks.js';
import Text from '../fields/recordm.fields.text.js';

import tmpl_clause from '../templates/search-advanced-clause.dot';
import tmpl_form from '../templates/search-advanced-form.dot';
import tmpl_operators from '../templates/search-advanced-operators.dot';

import utils from '@cob/ui-genesis/cob.utils.js';

// $container: the jquery HTML wrapper for the enclosing element
//
// callbacks:
// * `doSearch`: called when the user clicks the new search button or presses enter
// * `goToSimpleSearch`: The user requested to see the simple search
var AdvancedSearchV = function(core, $container, entityM, callbacks) {

    var _$advancedSearchForm = $(tmpl_form({ entityM: entityM })),
        _$searchButton = $(".js-advanced-search-btn", _$advancedSearchForm),
        _$searchFormContainer = $(".js-search-form-container", $container),
        _$searchEntriesContainer = $(".js-search-entries-container", _$advancedSearchForm);

    const fields = entityM.getSearchableFields({excludeLinks:true});
    const cloned = utils.clone(fields);
    for (let i = 0; i < cloned.length; i++) {
       // copy the formatters, so that they keep the original scope (from before beeing cloned)
       if (cloned[i].formatter) {
          cloned[i].formatter = fields[i].formatter;
       }
    }
    var _sortedFields = cloned.sort(function(col1, col2) {
        return col1.label.toLowerCase().localeCompare(col2.label.toLowerCase());
    });

    var _mainView = AdvancedSearchV.FieldsV(
        core,
        _$searchEntriesContainer,
        _sortedFields
    );

    var _addListenerToGoToSimpleSearch = function() {
        $container.on("click", ".js-simple-search-link", function(e) {
            e.preventDefault();

            callbacks.goToSimpleSearch();
        });
    };

    (function init() {
        core.translate("recordm.search", _$advancedSearchForm);

        $container.on('submit', '.js-advanced-search-form', function(e) {
            e.preventDefault();
            callbacks.doSearch(_mainView.getSearchParameters());
        });

        _addListenerToGoToSimpleSearch();
        _mainView.addRow();
    })();

    return{
        start: function() {
            _$searchFormContainer.html(_$advancedSearchForm);
        },
        stop: function() {
            _$advancedSearchForm.detach();
        },
        destroy: function() {
            $container.off('submit', '.js-advanced-search-form');

            _mainView.stop();
            _$advancedSearchForm.remove();
        },

        setSearchParameters: function(searchParameters) {
            // We need to clone the searchParameters object because this object is the same in
            // use in the search model and I'm going to use the pop method of the array,
            // so I reverse the array to fill the fields of the advance search.
            _mainView.setSearchParameters(utils.clone(searchParameters).reverse());
        },

        setFocusOnSearch: function() {
            _$searchButton.focus();
        }
    };
};

//
// View responsible to build and show each search row container.
//
// Each row is composed with :
// * A dropdown field that has multiple options that are the entity fields available.
// * One operator field that also have several options that are associated with the fieldtype from the the options field
// * A value field. Currently we support text fields, date, and dropdowns (Multivalues)
// * A list of buttons that will allow to add / remove fields to this container.
//
// Arguments:
//
// * `core` - instance of cob.app
// * `$container` -  a container to append the search rows
// * `fields` -  an array of fields. (Possibble values)
//
// The array of fields must be composed with objects that obey to the following format:
// `{ fieldName: ..., label: ..., type: recordm.entity.BaseM.fieldDataType...}`
//
AdvancedSearchV.FieldsV = function(core, $container, fields, confs) {

    var _confs = {
        // if the container can have no fields.
        // When it is the 1st level it should be false
        allowEmpty: false
    };

    // A map with the key the id and value fields
    var recordMFields = {};

    var _fieldsMap = (function(fields) {
        var map = {};

        fields.forEach(function(field) {
            map[field.fieldName] = field;
        });

        return map;

    })(fields);

    // Updates the visibility of the remove links of each row
    var _updateRemoveLinks = function() {

        // I'm only interested in the direct children of our container
        // and not in the lower children
        var $searchEntries = $container.children(".js-search-entry"),
            $removeBtnRow = $searchEntries.first()
                .find(".js-advanced-field-remove")
                .first();


        if ($searchEntries.length !== 1 || _confs.allowEmpty) {
            $removeBtnRow.css("visibility", "visible");

        } else {
            $removeBtnRow.css("visibility", "hidden");
        }
    };

    var _nameFieldChanged = function($searchRow) {
        var $nameField = $searchRow.find("select.js-entity-field :selected"),
            fieldType = $nameField.attr("data-field-type"),
            // HACK: passsamos o core.translateString em vez de usar o rel="localize[...]"
            $operatorsField = $(tmpl_operators({
               fieldType: fieldType,
               fieldTypes: BaseM.fieldDataType,
               operators: AdvancedSearchM.queryOperators,
               translate: core.translateString
            }));

        var fieldConf = {id: core.locallyUniqueId(), cssClasses: 'js-field-value search-field value-field'},
            $valueField;

        if (fieldType === BaseM.fieldDataType.DATE) {
            $valueField = new DateF(core, fieldConf);

        } else if (fieldType === BaseM.fieldDataType.DATETIME) {
            $valueField = new DateTime(core, fieldConf);

        } else if (fieldType === BaseM.fieldDataType.MULTIVALUE) {
            $valueField = new Select(
                core,
                _fieldsMap[$nameField.attr("value")].possibleValues,
                fieldConf
            );

        } else if (fieldType === BaseM.fieldDataType.NUMBER) {
            // we should be able to search with a reasonable number of decimal places
            $valueField = new NumberF(core, { ...fieldConf, decimalPlaces: 10 });

        } else if (fieldType === BaseM.fieldDataType.TASKS) {
            $valueField = new TasksSearch(core, fieldConf);

        } else {
            $valueField = new Text(core, fieldConf);

        }

        recordMFields[fieldConf.id] = $valueField;

        $searchRow.find(".js-field-operator").html($operatorsField);
        $searchRow.find(".js-field-value-wrapper").html($valueField.render());
    };

    // Add a new search row after another search row
    // if no $afterSearchRow provided then append to the $container
    var _addSearchRow = function(options, $afterSearchRow) {

        var $newSearchRow = $(tmpl_clause({
            options: options,

            // Usefull for jquery selectors when referecing the child container element
            childUniqueId: core.locallyUniqueId()
        }));

        // call the method that will allow to change the operators field
        // and the valueField.
        _nameFieldChanged($newSearchRow);

        if ($afterSearchRow) {
            // The user requested a new search row after another
            $newSearchRow.insertAfter($afterSearchRow);

        } else {
            $container.append($newSearchRow);
        }

        // update the visibility of the remove links
        _updateRemoveLinks();

        $newSearchRow.removeClass("hidden");

        return $newSearchRow;
    };

    var _destroyRecordMField = function($searchRow) {
        var fieldValueId = $searchRow.find(".js-field-value").attr("id"),
            recordMField = recordMFields[fieldValueId.replaceAll("f", "")];

        recordMField.destroy();
        delete recordMFields[fieldValueId];
    };

    var _removeSearchRow = function($searchRow) {
        _destroyRecordMField($searchRow);

        $searchRow.remove();
        _updateRemoveLinks();
    };

    (function init() {

        utils.extend(_confs, confs, true);

        // listener to add a search row after an element
        $container.on('click', ".js-advanced-field-add", function(e) {
            //don't allow the event to bubble
            e.stopPropagation();

             /* eslint-disable-next-line no-invalid-this  */
            _addSearchRow(fields, $(this).parents(".js-search-entry").first());
        });

        // listener to remove a search row
        $container.on('click', ".js-advanced-field-remove", function(e) {
            //don't allow the event to bubble
            e.stopPropagation();

             /* eslint-disable-next-line no-invalid-this  */
            _removeSearchRow($(this).parents(".js-search-entry").first());
        });

        $container.on('click', ".js-add-children-fields", function(e) {
            //don't allow the event to bubble
            e.stopPropagation();
        });

        $container.on('change', "select.js-entity-field", function(e) {
            //don't allow the event to bubble
            e.stopPropagation();

            /* eslint-disable-next-line no-invalid-this  */
            var $searchRow = $(this).parents(".js-search-entry").first();

            _destroyRecordMField($searchRow);
            _nameFieldChanged($searchRow);
        });
    })();

    return {
        addRow: function() {
            _addSearchRow(fields);
        },
        stop: function() {
            utils.forEachOwn(recordMFields, function(key) {
                recordMFields[key].destroy();
            });

            $container.off('click', ".js-advanced-field-add");
            $container.off('click', ".js-advanced-field-remove");
            $container.off('change', "select.js-entity-field");

            // Clear the container
            $container.empty();
        },

        getSearchParameters: function() {
            var searchParameters = [];

            $container.children(".js-search-entry").each(function(idx, el) {

                var nameField = $(".js-entity-field", el).val(),
                    typeField = $(".js-entity-field :selected", el).data('field-type'),
                    operatorField = $(".js-field-operator", el).val(),
                    valueFieldId = $(".js-field-value", el).attr("id"),
                    $valueField = recordMFields[valueFieldId.replaceAll("f", "")];

                searchParameters.push({
                   field: nameField,
                   type: typeField,
                   value: $valueField.getValue(),
                   operator: operatorField
                });
            });

            return searchParameters;
        },

        setSearchParameters: function(searchParameters) {

            if (searchParameters.length !== 0) {
                $container.empty();
            }

            var parameter;

            while (searchParameters.length > 0) {

                // Remove the field from the array
                parameter = searchParameters.pop();

                var $searchRow = _addSearchRow(fields),
                    $nameField = $(".js-entity-field", $searchRow),
                    fieldValueId,
                    $operatorField,
                    $valueField;

                if ($nameField.find("option[value='" + parameter.field + "']").length === 0) {
                    // if there is no option in the name field with this field then it must belong to the
                    // parent FieldsV container. In this case this works because all the service fields
                    // have different names from the entity fields.

                    // Undo all work that has been done and put the parameter in the last place of the array.
                    _removeSearchRow($searchRow);
                    searchParameters.push(parameter);

                    return;
                }

                $nameField.val(parameter.field);

                // "trigger change" so the operators list is repopulated with the appropriate operators
                _nameFieldChanged($searchRow);

                $operatorField = $(".js-field-operator", $searchRow);
                $operatorField.val(parameter.operator);

                fieldValueId = $(".js-field-value", $searchRow).attr("id");
                $valueField = recordMFields[fieldValueId.replaceAll("f", "")];
                $valueField.setValue(parameter.value);
            }
        }
    };
};

export default AdvancedSearchV;
